/**
 * Zeus Board Selector — shared bento modal (quiz results + carousel).
 */
(function () {
  "use strict";

  function extractSectionId(element) {
    const section = element?.classList?.contains("shopify-section") ? element : element?.closest?.(".shopify-section");
    return section ? section.id.replace("shopify-section-", "") : null;
  }

  function focusables(root) {
    return Array.from(
      root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null || el.getClientRects().length > 0);
  }

  class ZeusBselModal {
    #abort;

    constructor(root, options) {
      this.root = root;
      this.options = options || {};
      this.bentoSectionId = this.options.bentoSectionId || this.#resolveBentoSectionId();
      this.bentoCache = new Map();
      this.bentoLoading = new Map();
      this.wiredBentos = new WeakSet();
      this.open = false;
      this.lastFocus = null;
      this.declineFocus = null;
      this.prevBodyOverflow = "";
      this.#abort = new AbortController();
      this.#bind();
    }

    destroy() {
      this.#abort.abort();
    }

    #resolveBentoSectionId() {
      const marker = document.querySelector("[data-bsel-bento-api-section-id]");
      if (marker) return marker.getAttribute("data-bsel-bento-api-section-id");
      const partial = document.querySelector(".zeus-bsel-bento-partial-section");
      if (partial) return extractSectionId(partial);
      return "zeus-bsel-bento-partial";
    }

    #el(sel) {
      return this.root.querySelector(sel);
    }

    #bind() {
      const signal = this.#abort.signal;
      this.#el("[data-bsel-backdrop]")?.addEventListener("click", () => this.close(), { signal });
      this.#el("[data-bsel-mclose]")?.addEventListener("click", () => this.close(), { signal });
      this.#el("[data-bsel-decline-stay]")?.addEventListener("click", () => this.#closeDecline(), { signal });
      this.#el("[data-bsel-decline-leave]")?.addEventListener(
        "click",
        () => {
          const btn = this.#el("[data-bsel-decline-leave]");
          if (btn) btn.textContent = this.root.dataset.declineLeaveDone || "";
          window.setTimeout(() => this.#closeDecline(), 1100);
        },
        { signal },
      );
      this.#el("[data-bsel-decline-overlay]")?.addEventListener(
        "click",
        (e) => {
          if (e.target.matches("[data-bsel-decline-overlay]")) this.#closeDecline();
        },
        { signal },
      );

      document.addEventListener(
        "keydown",
        (e) => {
          if (!this.open && !this.#el("[data-bsel-decline-overlay].is-open")) return;
          if (e.key === "Escape") {
            if (this.#el("[data-bsel-decline-overlay].is-open")) this.#closeDecline();
            else if (this.open) this.close();
            return;
          }
          const decline = this.#el("[data-bsel-decline-overlay].is-open");
          if (decline) {
            this.#trapFocus(e, decline.querySelector(".zeus-bsel__decline-card") || decline);
          } else if (this.open) {
            this.#trapFocus(e, this.#el(".zeus-bsel__sheet") || this.root);
          }
        },
        { signal },
      );
    }

    #trapFocus(e, root) {
      if (e.key !== "Tab" || !root) return;
      const items = focusables(root);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    #lockScroll() {
      if (document.body.style.overflow !== "hidden") this.prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    #unlockScroll() {
      if (this.open || this.#el("[data-bsel-decline-overlay].is-open")) return;
      document.body.style.overflow = this.prevBodyOverflow;
    }

    async #loadBento(productUrl, handle) {
      const key = handle || productUrl;
      if (this.bentoCache.has(key)) return this.bentoCache.get(key);
      if (this.bentoLoading.has(key)) return this.bentoLoading.get(key);

      const promise = (async () => {
        const url = `${productUrl}?section_id=${this.bentoSectionId}`;
        const response = await fetch(url, { priority: "low" });
        if (!response.ok) throw new Error(`Bento fetch failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const panel = doc.querySelector("[data-bsel-bento]");
        if (!panel) throw new Error("Bento panel missing");
        panel.hidden = true;
        this.bentoCache.set(key, panel);
        this.#wireBento(panel);
        this.bentoLoading.delete(key);
        return panel;
      })();

      this.bentoLoading.set(key, promise);
      promise.catch(() => this.bentoLoading.delete(key));
      return promise;
    }

    #wireBento(panel) {
      if (this.wiredBentos.has(panel)) return;
      this.wiredBentos.add(panel);

      const declineBtn = panel.querySelector("[data-bsel-decline-open]");
      if (declineBtn && this.root.dataset.declineEnabled !== "false") {
        declineBtn.addEventListener("click", () => this.#openDecline(panel));
      }

      const form = panel.querySelector("[data-bsel-form]");
      if (form) {
        form.addEventListener("submit", (e) => this.#onAtcSubmit(e, form));
        form.addEventListener("variant:change", (e) => this.#onVariantChange(panel, e));
      }
    }

    #openDecline(panel) {
      const overlay = this.#el("[data-bsel-decline-overlay]");
      if (!overlay) return;
      const tag = overlay.querySelector("[data-bsel-decline-tag]");
      const title = overlay.querySelector("[data-bsel-decline-title]");
      const sub = overlay.querySelector("[data-bsel-decline-sub]");
      const body = overlay.querySelector("[data-bsel-decline-body]");
      const template = panel.querySelector("[data-decline-body-template]");
      if (tag) tag.textContent = panel.dataset.declineTag || "";
      if (title) title.textContent = panel.dataset.declineTitle || "";
      if (sub) sub.textContent = panel.dataset.declineSub || "";
      if (body && template) body.innerHTML = template.innerHTML;
      this.#lockScroll();
      overlay.classList.add("is-open");
      overlay.setAttribute("aria-hidden", "false");
      this.declineFocus = document.activeElement;
      overlay.querySelector("[data-bsel-decline-stay]")?.focus();
    }

    #closeDecline() {
      const overlay = this.#el("[data-bsel-decline-overlay]");
      if (!overlay) return;
      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
      this.#unlockScroll();
      if (this.declineFocus) {
        this.declineFocus.focus();
        this.declineFocus = null;
      }
    }

    async #onAtcSubmit(event, form) {
      event.preventDefault();
      const atc = form.querySelector("[data-bsel-atc]");
      const variantId = form.querySelector("[data-bsel-variant-id]");
      if (!variantId?.value) return;
      if (atc) {
        atc.disabled = true;
        atc.setAttribute("data-loading", "1");
      }
      try {
        const cartUrl = window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : window.routes?.cart_add_url
            ? `${window.routes.cart_add_url}.js`
            : "/cart/add.js";
        const res = await fetch(cartUrl, {
          method: "POST",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: new FormData(form),
        });
        if (!res.ok) throw await res.json();
        if (atc?.dataset.atcSuccess) {
          const original = atc.textContent;
          atc.textContent = atc.dataset.atcSuccess;
          window.setTimeout(() => {
            atc.textContent = original;
          }, 1600);
        }
        this.close();
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        document.querySelector("cart-drawer")?.show?.();
      } catch (err) {
        console.warn("[zeus-bsel-modal] ATC failed", err);
        const msg = err?.description || err?.message || this.root.dataset.bselAtcError || "Could not add to cart.";
        window.alert(msg);
      } finally {
        if (atc) {
          atc.disabled = false;
          atc.removeAttribute("data-loading");
        }
      }
    }

    #onVariantChange(panel, event) {
      const variant = event.detail?.variant;
      if (!variant) return;
      const form = panel.querySelector("[data-bsel-form]");
      const variantInput = form?.querySelector("[data-bsel-variant-id]");
      const atc = form?.querySelector("[data-bsel-atc]");
      const bisWrap = form?.querySelector("[data-bsel-bis-wrap]");
      const priceEl = panel.querySelector("[data-bsel-price]");
      if (variantInput) variantInput.value = variant.id;
      if (priceEl && variant.price != null) {
        priceEl.textContent = window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(variant.price)
          : String(variant.price);
      }
      if (atc) atc.hidden = !variant.available;
      if (bisWrap) bisWrap.hidden = variant.available;
    }

    #animatePerfBars(panel) {
      panel.querySelectorAll(".zeus-bsel__tile.perf .rf, .tile.perf .rf").forEach((bar) => {
        bar.style.width = "0";
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.querySelectorAll(".zeus-bsel__tile.perf .rf, .tile.perf .rf").forEach((bar) => {
            bar.style.width = "";
          });
        });
      });
    }

    async #showBento(productUrl, handle) {
      const host = this.#el("[data-bsel-bento-host]");
      const loading = this.#el("[data-bsel-bento-loading]");
      if (!host) return;
      const key = handle || productUrl;
      const cached = this.bentoCache.get(key);
      host.classList.remove("is-error");
      if (cached) {
        host.classList.remove("is-loading");
        if (loading) loading.hidden = true;
      } else {
        host.classList.add("is-loading");
        if (loading) {
          loading.hidden = false;
          const text = loading.querySelector(".zeus-bsel__bento-loading-text");
          if (text) {
            text.textContent = this.root.dataset.bselBentoLoading || text.dataset.defaultText || text.textContent;
          }
        }
      }
      try {
        const panel = cached || (await this.#loadBento(productUrl, handle));
        host.querySelectorAll("[data-bsel-bento]").forEach((el) => {
          el.hidden = true;
        });
        if (!host.contains(panel)) host.appendChild(panel);
        panel.hidden = false;
        host.classList.remove("is-loading");
        if (loading) loading.hidden = true;
        host.classList.add("is-swap");
        requestAnimationFrame(() => host.classList.remove("is-swap"));
        this.#animatePerfBars(panel);
      } catch (err) {
        console.warn("[zeus-bsel-modal] bento load failed", err);
        host.classList.remove("is-loading");
        host.classList.add("is-error");
        if (loading) {
          loading.hidden = false;
          const text = loading.querySelector(".zeus-bsel__bento-loading-text");
          if (text) text.textContent = this.root.dataset.bselBentoError || "";
        }
      }
    }

    async openProduct(productUrl, handle) {
      const modal = this.#el("[data-bsel-modal]");
      if (!modal || !productUrl) return;
      await this.#showBento(productUrl, handle);
      this.open = true;
      this.#lockScroll();
      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");
      this.lastFocus = document.activeElement;
      this.#el("[data-bsel-mclose]")?.focus();
    }

    close() {
      this.open = false;
      const modal = this.#el("[data-bsel-modal]");
      if (!modal) return;
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      this.#unlockScroll();
      if (this.lastFocus) {
        this.lastFocus.focus();
        this.lastFocus = null;
      }
    }
  }

  window.ZeusBselModal = ZeusBselModal;
})();
